import {Banner, Link} from '@primer/react'

import type {WeakPasswordBannerPayload} from './types'

const longDateFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'long',
  timeZone: 'UTC',
})

/**
 * Warns a viewer that a password they submitted appears in a list of passwords
 * commonly used on other websites.
 */
export function WeakPasswordBanner(payload: WeakPasswordBannerPayload) {
  const {context, documentationUrl} = payload

  let message = (
    <>
      The new password you provided is in a list of passwords commonly used on other websites. Your password was not
      saved. Please try again with a stronger password.
    </>
  )

  if (context === 'signed_in') {
    const {blockDeadlineAt, settingsSecurityUrl} = payload
    const blockDeadline = longDateFormatter.format(new Date(blockDeadlineAt))

    message = (
      <>
        The password you provided is in a list of passwords commonly used on other websites. To increase your security,{' '}
        you must update your password. <strong>After {blockDeadline} we will automatically reset your password.</strong>{' '}
        Change your password in your{' '}
        <Link inline href={settingsSecurityUrl}>
          Password and authentication settings
        </Link>
        .
      </>
    )
  } else if (context === 'sign_in_incomplete') {
    const {blockDeadlineAt} = payload
    const blockDeadline = longDateFormatter.format(new Date(blockDeadlineAt))

    message = (
      <>
        The password you provided is in a list of passwords commonly used on other websites. To increase your security,{' '}
        <strong>you will be unable to sign in using your current password after {blockDeadline}</strong>. Please
        complete your two-factor authentication process and change your password as soon as possible.
      </>
    )
  }

  return (
    <Banner
      variant={context === 'creation_or_change' ? 'warning' : 'critical'}
      title="Weak password"
      hideTitle
      description={
        <>
          <p>{message}</p>
          <p>
            Read our documentation on{' '}
            <Link inline href={documentationUrl}>
              safer password practices
            </Link>
            .
          </p>
        </>
      }
      flush
    />
  )
}
